// PackProgressSheet — per-category download progress for the Pack
// flow.
//
// Lives inside PlanPanel; only renders when there's an active or
// recently-completed pack. Shows one row per non-empty category with
// a progress bar, OK/FAIL counts, and a Retry button when items are
// stuck in a failed state. Mirrors ForeFlight's per-download surface
// so you can tell exactly what's missing instead of just 'one item
// failed'.

import {
  usePackStore,
  PACK_CATEGORY_LABEL,
  formatBytes,
  type PackCategory,
  type PackCategoryProgress,
} from '../lib/pack';
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  RotateCw,
} from '../shell/Icon';

/**
 * Props:
 *   • `scopeFlightId` — only render when the cached pack manifest was
 *     started for this flight. Set by FlightDetailPage so the
 *     previous flight's completed pack doesn't "appear already
 *     packed" on a fresh flight (bug reported 2026-05-12). Omit for
 *     global surfaces (PlanPanel) that should show whatever pack is
 *     most recent.
 */
export default function PackProgressSheet({
  scopeFlightId,
}: { scopeFlightId?: string } = {}) {
  const current = usePackStore((s) => s.current);
  const phase   = usePackStore((s) => s.phase);
  const retryCategory = usePackStore((s) => s.retryCategory);
  const retryAllFailed = usePackStore((s) => s.retryAllFailed);

  if (!current || !current.categories || current.categories.length === 0) return null;
  // Scope guard: when the caller pinned a flight id, hide the sheet
  // when the current pack belongs to a different flight. A pack that
  // is actively running is always shown so an in-progress download's
  // status never disappears mid-way.
  const isBusy = phase === 'planning' || phase === 'downloading';
  if (
    !isBusy &&
    scopeFlightId !== undefined &&
    current.scopeFlightId !== scopeFlightId
  ) {
    return null;
  }

  const downloading = phase === 'downloading' || phase === 'planning';
  const totalFailed = current.categories.reduce((s, c) => s + c.fail, 0);

  // Size estimate: shown alongside the title while downloading (so the
  // pilot sees the budget BEFORE the first byte arrives) and as the
  // post-download actual when phase === 'ready'.
  const estMb = current.estMb;
  const tileCount = current.tileCount;
  const showEstimate = downloading && typeof estMb === 'number';

  return (
    <div className="w-full border border-cockpit-600 rounded-lg p-2.5 space-y-2 bg-cockpit-800/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[11px] font-semibold text-cockpit-300 uppercase tracking-wider">
            {downloading ? 'Packing…' : phase === 'ready' && totalFailed === 0 ? 'Pack ready'
              : phase === 'ready' || phase === 'failed' ? `Pack: ${totalFailed} failed`
              : 'Pack'}
          </div>
          {showEstimate && (
            <div
              className="text-[10px] text-cockpit-400 font-mono"
              title={`Estimated download size — ${tileCount} tile URLs + ${current.total - (tileCount ?? 0)} API calls`}
            >
              Est: ~{estMb} MB
              {typeof tileCount === 'number' && (
                <span> · {tileCount.toLocaleString()} tiles</span>
              )}
            </div>
          )}
        </div>
        {totalFailed > 0 && !downloading && (
          <button
            onClick={() => void retryAllFailed()}
            className="flex items-center gap-1 text-[11px] text-ember-400 hover:text-ember-300"
            aria-label="Retry all failed items"
          >
            <RotateCw size={11} />
            <span>Retry all</span>
          </button>
        )}
      </div>

      <ul className="space-y-1.5">
        {current.categories.map((c) => (
          <CategoryRow
            key={c.category}
            cat={c}
            onRetry={() => void retryCategory(c.category)}
            busy={downloading}
          />
        ))}
      </ul>

      <div className="text-[10px] text-cockpit-400 flex justify-between pt-0.5 border-t border-cockpit-700">
        <span>
          {current.ok + current.fail} / {current.total} · {formatBytes(current.bytes)}
        </span>
        {totalFailed > 0 && (
          <span className="text-amber-400">{totalFailed} failed</span>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  cat: PackCategoryProgress;
  onRetry: () => void;
  busy: boolean;
}

function CategoryRow({ cat, onRetry, busy }: RowProps) {
  const done = cat.ok + cat.fail;
  const pct = cat.total > 0 ? Math.round((done / cat.total) * 100) : 0;
  const inProgress = done < cat.total;
  const hasFailures = cat.fail > 0;

  return (
    <li className="space-y-0.5">
      <div className="flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {inProgress ? (
            <Loader2 size={12} className="text-cockpit-400 animate-spin flex-shrink-0" />
          ) : hasFailures ? (
            <AlertTriangle size={12} className="text-amber-400 flex-shrink-0" />
          ) : (
            <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
          )}
          <span className="truncate text-cockpit-200">{PACK_CATEGORY_LABEL[cat.category]}</span>
        </div>
        <div className="text-[10px] font-mono text-cockpit-400 flex items-center gap-1.5">
          <span>{cat.ok}/{cat.total}</span>
          {hasFailures && !inProgress && !busy && (
            <button
              onClick={onRetry}
              className="flex items-center gap-0.5 text-ember-400 hover:text-ember-300"
              aria-label={`Retry ${PACK_CATEGORY_LABEL[cat.category]}`}
            >
              <RotateCw size={10} />
              <span>retry</span>
            </button>
          )}
        </div>
      </div>
      <div className="h-1 rounded-full bg-cockpit-800 overflow-hidden">
        <div
          className={[
            'h-full transition-all',
            hasFailures && !inProgress ? 'bg-amber-500'
              : inProgress ? 'bg-ember-500'
              : 'bg-emerald-500',
          ].join(' ')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </li>
  );
}

// Re-export for callers that want the labels (e.g. analytics).
export { PACK_CATEGORY_LABEL };
export type { PackCategory };
